import React, { useState, useEffect } from 'react';
import { useParams, Navigate, useLocation, Link } from 'react-router-dom';
import { SEO, Hero, Section, CTA } from '../components/Shared';
import QRGenerator from '../components/QRGenerator';
import { contentRegistry } from '../contentRegistry';
import { getArcticCoachUrl } from '../constants/config';

const ContentPage: React.FC = () => {
  const { slug: paramsSlug } = useParams<{ slug: string }>();
  const location = useLocation();
  const slug = paramsSlug || location.pathname.substring(1).split('/').pop() || "";
  const content = contentRegistry[slug];

  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const storageKey = `lail-progress-${slug}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        const checkedCount = Object.values(data).filter(Boolean).length;
        const totalCount = content?.checklist.length || 1;
        setProgress(Math.round((checkedCount / totalCount) * 100));
      } catch (e) {
        console.error("Error parsing progress", e);
      }
    }
  }, [slug, content]);

  if (!content) return <Navigate to="/" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <SEO title={content.seo.title} description={content.seo.description} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left: Content (8 cols) */}
        <div className="lg:col-span-8 space-y-12">
          <div className="flex items-center gap-4 mb-8">
            <span className="px-3 py-1 rounded-full bg-slate-900/60 border border-white/10 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
              {content.category} v{content.version}
            </span>
          </div>

          <Hero title={content.hero.title} subtitle={content.hero.subtitle} label="Arctic Deep Dive" />

          {content.sections.map((section, idx) => (
            <Section 
              key={section.id}
              title={section.title}
              slug={slug}
              checklist={idx === content.sections.length - 1 ? content.checklist : undefined}
              pitfalls={idx === content.sections.length - 1 ? content.pitfalls : undefined}
              colorClass={idx % 2 === 0 ? "bg-teal-400" : "bg-purple-400"}
            >
              {section.body}
            </Section>
          ))}
          
          {content.cta.route && (
            <CTA label={content.cta.text} link={content.cta.route} />
          )}
        </div>

        {/* Right: Tools & Progress (4 cols) */}
        <div className="lg:col-span-4">
          <div className="sticky top-32 space-y-8">
            {/* Progress Card */}
            <div className="arctic-panel p-8 rounded-[2.5rem] border-white/10 shadow-xl">
              <h4 className="text-white font-black text-xs uppercase tracking-widest mb-6">Moduulin eteneminen</h4>
              <div className="w-full h-2 bg-slate-900 rounded-full overflow-hidden mb-4">
                <div className="h-full bg-teal-400 transition-all duration-1000" style={{ width: `${progress}%` }}></div>
              </div>
              <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                <span className="text-slate-500">{progress}% Valmis</span>
                {progress === 100 && <span className="text-teal-300">Auditoitu</span>}
              </div>
            </div>

            {/* QR Code Tool (New) */}
            {content.qrCode && (
              <QRGenerator 
                value={content.qrCode.value}
                label={content.qrCode.label}
                description={content.qrCode.description}
              />
            )}

            {/* Arctic Coach Tool Card */}
            <a 
              href={getArcticCoachUrl('content')} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="block group"
            >
              <div className="glass p-8 rounded-[2.5rem] border-amber-500/20 group-hover:border-amber-500/50 transition-all bg-amber-500/5 shadow-xl relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-24 h-24 bg-amber-500/5 rounded-full blur-2xl group-hover:bg-amber-500/10 transition-colors"></div>
                <h4 className="text-amber-400 font-black text-xs uppercase tracking-widest mb-2">Arctic Coach</h4>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">Kysy neuvoa tai pyydä lisäesimerkkejä tästä moduulista.</p>
                <div className="flex items-center text-amber-400 font-black text-[10px] uppercase tracking-widest">
                  Avaa valmentaja
                  <svg className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
              </div>
            </a>

            {/* Quiz Link */}
            {content.quizRef && (
              <Link to={`/quiz/${content.quizRef}`} className="block group">
                <div className="arctic-panel p-8 rounded-[2.5rem] border-teal-400/20 group-hover:border-teal-400/50 transition-all bg-teal-500/5 shadow-xl">
                  <h4 className="text-teal-300 font-black text-xs uppercase tracking-widest mb-2">Osaamistesti</h4>
                  <p className="text-slate-400 text-sm leading-relaxed mb-6">Vahvista osaamisesi ja ansaitse badge tästä moduulista.</p>
                  <div className="flex items-center text-teal-300 font-black text-[10px] uppercase tracking-widest">
                    Aloita tentti
                    <svg className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </Link>
            )}

            {/* Quick Actions if available */}
            {content.quickActions && (
              <div className="arctic-panel p-8 rounded-[2.5rem] border-white/10 shadow-xl">
                <h4 className="text-white font-black text-xs uppercase tracking-widest mb-6">Pikatoimet tänään</h4>
                <ul className="space-y-4">
                  {content.quickActions.today.map((action, i) => (
                    <li key={i} className="text-xs text-slate-400 flex items-start gap-3 leading-snug">
                      <span className="w-1.5 h-1.5 bg-teal-400 rounded-full mt-1.5 shrink-0"></span>
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentPage;
